import { useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "pdfjs-dist/build/pdf.worker.min.mjs";
// import "react-pdf/dist/esm/Page/AnnotationLayer.css";
// import "react-pdf/dist/esm/Page/TextLayer.css";
import { useTranslation } from "react-i18next";
import useWindowDimensions from "../hooks/useWindowDimensions";
import ScrollContainer from "react-indiana-drag-scroll";
import LoadingOverlay from "./LoadingOverlay";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/build/pdf.worker.min.mjs",
    import.meta.url,
).toString();

const PDFViewer = ({ file }) => {
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [scale, setScale] = useState(1);
    const { t } = useTranslation();
    const { width } = useWindowDimensions();

    function onDocumentLoadSuccess({ numPages }) {
        setNumPages(numPages);
        setIsLoading(false);
    }

    function onDocumentLoadError(err) {
        console.error("Error loading PDF:", err);
        setIsLoading(false);
        setError(
            `${t("error_loading_pdf") || "Error loading PDF"}: ${err.message}`,
        );
    }

    // Modified zoom functions to handle errors
    const zoomIn = () => {
        try {
            setScale((prevScale) => Math.min(prevScale * 1.5, 8));
        } catch (err) {
            console.error("Error zooming in:", err);
        }
    };

    const zoomOut = () => {
        try {
            setScale((prevScale) => Math.max(prevScale / 1.5, 0.25));
        } catch (err) {
            console.error("Error zooming out:", err);
        }
    };

    // Page navigation without setting pageLoading state
    const goToNextPage = () => {
        if (pageNumber < numPages) {
            setPageNumber(pageNumber + 1);
        }
    };

    const goToPreviousPage = () => {
        if (pageNumber > 1) {
            setPageNumber(pageNumber - 1);
        }
    };

    return (
        <div className="pdf-reader">
            <LoadingOverlay active={isLoading} spinner text={t("loading")} />

            {error ? (
                <div className="pdf-error">
                    <p>{error}</p>
                    <p>{t("please_try_again_later")}</p>
                </div>
            ) : (
                <>
                    <div className="pdf-reader__actions">
                        <div className="pdf-reader__zoom">
                            <button onClick={zoomOut} type="button">
                                <i className="vnf-icons-icon-m-dezoom-outline"></i>
                                <span className="sr-only">{t("zoom_out")}</span>
                            </button>
                            <button onClick={zoomIn} type="button">
                                <i className="vnf-icons-icon-m-zoom-outline"></i>
                                <span className="sr-only">{t("zoom_in")}</span>
                            </button>
                        </div>

                        <div className="pdf-reader__pages">
                            <button
                                type="button"
                                disabled={pageNumber === 1}
                                onClick={goToPreviousPage}
                            >
                                <i className="vnf-icons-icon-fleche-a-gauche-outline"></i>
                                <span className="sr-only">
                                    {t("go_to_previous_page")}
                                </span>
                            </button>
                            <span>
                                {pageNumber} / {numPages || 1}
                            </span>
                            <button
                                type="button"
                                disabled={pageNumber === numPages || !numPages}
                                onClick={goToNextPage}
                            >
                                <i className="vnf-icons-icon-m-fleche-a-droite-outline"></i>
                                <span className="sr-only">
                                    {t("go_to_next_page")}
                                </span>
                            </button>
                        </div>

                        <div className="pdf-reader__download">
                            <button
                                type="button"
                                onClick={() => window.open(file)}
                            >
                                <i className="vnf-icons-icon-m-imprimante-outline"></i>
                                <span className="sr-only">{t("print")}</span>
                            </button>
                            <a
                                className="pdf-reader__download"
                                href={file}
                                target="_blank"
                                rel="noreferrer"
                                data-tip={t("download_file")}
                            >
                                <i className="vnf-icons-icon-m-telechargement-outline"></i>
                                <span className="sr-only">
                                    {t("download_file")}
                                </span>
                            </a>
                        </div>
                    </div>
                    <ScrollContainer className="pdf-reader__document">
                        <Document
                            file={file}
                            renderMode="svg"
                            onLoadSuccess={onDocumentLoadSuccess}
                            onLoadError={onDocumentLoadError}
                            loading={null}
                        >
                            {!isLoading && (
                                <Page
                                    key={`page_${pageNumber}`}
                                    pageNumber={pageNumber}
                                    scale={scale}
                                    width={Math.min(width - 30, 800)}
                                    renderTextLayer={false}
                                    renderAnnotationLayer={true}
                                    loading={
                                        <div className="page-loading"></div>
                                    }
                                    noData={null}
                                    error={null}
                                />
                            )}
                        </Document>

                        {isLoading && (
                            <div className="pdf-loading">
                                {t("loading_document")}
                            </div>
                        )}
                    </ScrollContainer>
                </>
            )}
        </div>
    );
};

export default PDFViewer;
